"use client"

import { cn } from "@ui/utils"
import { Check, Lock } from "lucide-react"

export interface StepperStep {
  id: string
  title: string
  locked?: boolean
}

interface StepperProps {
  steps: StepperStep[]
  currentStepIndex: number
  className?: string
}

export function Stepper({ steps, currentStepIndex, className }: StepperProps) {
  return (
    <div className={cn("flex flex-col", className)}>
      {steps.map((step, index) => {
        const isCompleted = index < currentStepIndex
        const isCurrent = index === currentStepIndex
        const isLast = index === steps.length - 1
        const isLocked = step.locked

        return (
          <div key={step.id} className="relative mb-6 flex items-start last:mb-0">
            {/* Connecting line */}
            {!isLast && (
              <div
                className={cn(
                  "absolute top-5 -bottom-4 left-3 w-0 -translate-x-1/2 border-l-2",
                  isCompleted || isLocked ? "border-border-accent" : "border-border-accent border-dotted"
                )}
              />
            )}

            {/* Circle indicator */}
            <div className="relative mr-4 ml-1 flex flex-col items-center">
              <div
                className={cn(
                  "relative z-10 flex h-4 w-4 items-center justify-center rounded-full",
                  isLocked
                    ? "bg-muted text-muted-foreground"
                    : isCompleted
                      ? "bg-primary text-primary-foreground"
                      : isCurrent
                        ? "border-primary border-2"
                        : "border-border-accent border-2"
                )}
              >
                {isLocked && <Lock className="size-2" strokeWidth={3} />}
                {!isLocked && isCompleted && <Check className="size-2.5" strokeWidth={3} />}
                {!isLocked && isCurrent && <div className="bg-primary h-1.5 w-1.5 rounded-full" />}
              </div>
            </div>

            {/* Step content */}
            <div className={cn("-mt-0.5 flex-1", isLocked && "opacity-50")}>
              <p
                className={cn(
                  "text-sm font-medium",
                  isLocked
                    ? "text-muted-foreground"
                    : isCompleted || isCurrent
                      ? "text-foreground"
                      : "text-muted-foreground"
                )}
              >
                {step.title}
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}
